import axios from "axios";

/**
 * API base URL resolution.
 *
 * In production we want the API call to stay on the same origin as the
 * frontend the user loaded (e.g. link.hermesmobility.com → /api there, not
 * the preview URL). Emergent's platform proxies /api/* to the FastAPI
 * backend for every domain pointed at the app, so same-origin works out
 * of the box — AND avoids CORS + credential issues entirely.
 *
 * The REACT_APP_BACKEND_URL env var stays as a fallback for SSR / build
 * tooling / local development where window isn't defined or /api isn't
 * proxied locally.
 */
const ENV_BACKEND = process.env.REACT_APP_BACKEND_URL;

function resolveBase() {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  return ENV_BACKEND || "";
}

export const API = `${resolveBase()}/api`;

/**
 * Authenticated API client.
 *
 * Auth: the backend sets an httpOnly `access_token` cookie on register/login.
 * Same-origin requests send it automatically; `withCredentials: true` keeps
 * that guarantee even if the browser ever rewrites the origin.
 */
export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  // Backend gated endpoints (e.g. require_approved_rider) raise structured
  // detail like { code: "WAITLIST", message: "...", access_status: "..." }.
  // Surface the human message instead of falling through to "[object Object]".
  if (detail && typeof detail.message === "string") return detail.message;
  if (detail && typeof detail.error === "string") return detail.error;
  return "Something went wrong. Please try again.";
}
